import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { supabase, isSupabaseConnected } from "@/lib/supabase";

interface User {
  id: string;
  email: string;
  role: "admin" | "user";
  name?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  signIn: (email: string, password: string) => Promise<{ error?: string; user?: User }>;
  signUp: (email: string, password: string, name: string) => Promise<{ error?: string; user?: User }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

const HARDCODED_ADMINS: Record<string, { password: string; name: string }> = {
  "mohit@expertadvise.in": { password: "123456", name: "Mohit Admin" },
  "raj@expertadvise.in": { password: "123456", name: "Raj Admin" },
};

// Hardcoded admin accounts that work even when Supabase auth is connected
// but the users haven't been created via the setup page
const HARDCODED_ADMIN_USERS: Record<string, User> = {
  "mohit@expertadvise.in": { id: "hc-admin-mohit", email: "mohit@expertadvise.in", role: "admin", name: "Mohit Admin" },
  "raj@expertadvise.in": { id: "hc-admin-raj", email: "raj@expertadvise.in", role: "admin", name: "Raj Admin" },
};

function getMockUser(): User | null {
  try {
    const stored = localStorage.getItem("mock_auth_user");
    if (!stored) return null;
    return JSON.parse(stored);
  } catch {
    return null;
  }
}

function setMockUser(user: User | null) {
  if (user) localStorage.setItem("mock_auth_user", JSON.stringify(user));
  else localStorage.removeItem("mock_auth_user");
}

async function fetchProfile(userId: string): Promise<{ role: "admin" | "user"; name?: string } | null> {
  if (!supabase) return null;
  const { data, error } = await supabase
    .from("profiles")
    .select("role, name")
    .eq("id", userId)
    .maybeSingle();
  if (error || !data) return null;
  return { role: data.role as "admin" | "user", name: data.name };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (isSupabaseConnected && supabase) {
      supabase.auth.getSession().then(async ({ data: { session } }) => {
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            role: profile?.role || "user",
            name: profile?.name || session.user.user_metadata?.name,
          });
        }
        setIsLoading(false);
      });

      const { data: listener } = supabase.auth.onAuthStateChange(async (_event, session) => {
        if (session?.user) {
          const profile = await fetchProfile(session.user.id);
          setUser({
            id: session.user.id,
            email: session.user.email || "",
            role: profile?.role || "user",
            name: profile?.name || session.user.user_metadata?.name,
          });
        } else {
          setUser(null);
        }
      });

      return () => {
        listener.subscription.unsubscribe();
      };
    } else {
      // Mock auth fallback
      const mock = getMockUser();
      setUser(mock);
      setIsLoading(false);
    }
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    // Check hardcoded admin accounts first (works even if Supabase user doesn't exist)
    const hardcodedAdmin = HARDCODED_ADMINS[email];
    if (hardcodedAdmin && hardcodedAdmin.password === password) {
      const userData = HARDCODED_ADMIN_USERS[email];
      setMockUser(userData);
      setUser(userData);
      return { user: userData };
    }

    if (isSupabaseConnected && supabase) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) return { error: error.message };
      if (data.user) {
        const profile = await fetchProfile(data.user.id);
        const userData = {
          id: data.user.id,
          email: data.user.email || "",
          role: profile?.role || "user",
          name: profile?.name || data.user.user_metadata?.name,
        };
        setUser(userData);
        return { user: userData };
      }
      return {};
    }

    // Legacy mock fallback for admin@expertadvise.in
    const MOCK_ADMIN = { id: "mock-admin-1", email: "admin@expertadvise.in", role: "admin" as const, name: "Admin User" };
    if (email === "admin@expertadvise.in" && password === "admin123") {
      setMockUser(MOCK_ADMIN);
      setUser(MOCK_ADMIN);
      return { user: MOCK_ADMIN };
    }
    return { error: "Invalid email or password. Try mohit@expertadvise.in / 123456 or raj@expertadvise.in / 123456" };
  }, []);

  const signUp = useCallback(async (email: string, password: string, name: string) => {
    if (isSupabaseConnected && supabase) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { name, role: "user" } },
      });
      if (error) return { error: error.message };
      if (data.user) {
        // Profile auto-created by trigger, fetch it
        const profile = await fetchProfile(data.user.id);
        const userData = {
          id: data.user.id,
          email: data.user.email || "",
          role: profile?.role || "user",
          name: profile?.name || name,
        };
        setUser(userData);
        return { user: userData };
      }
      return {};
    }

    // Mock sign up
    const newUser: User = { id: `mock-${Date.now()}`, email, role: "user", name };
    setMockUser(newUser);
    setUser(newUser);
    return { user: newUser };
  }, []);

  const signOut = useCallback(async () => {
    if (isSupabaseConnected && supabase) {
      await supabase.auth.signOut();
    }
    setMockUser(null);
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAdmin: user?.role === "admin",
        signIn,
        signUp,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}